import DropZone from "./DropZone";
import GetApp from "@material-ui/icons/GetApp";
import { GutterlessListItem } from "./lists";
import IconButton from "@material-ui/core/IconButton";
import ListItem from "@material-ui/core/ListItem";
import OpenInBrowser from "@material-ui/icons/OpenInBrowser";
import React from "react";
import Typography from "@material-ui/core/Typography";
import _ from "lodash";
import api from "../api";
import { makeStyles } from "@material-ui/core/styles";
import { useToastError } from "./FormError";
import { toast } from "react-toastify";
import { Logger } from "../lithic/logger";

const logger = new Logger("document-upload");

// Single source of truth for supported file types
const FILE_TYPE_CONFIG = [
  { mime: 'text/plain', extensions: ['.txt'] },
  { mime: 'application/pdf', extensions: ['.pdf'] },
  { mime: 'image/png', extensions: ['.png'] },
  { mime: 'image/jpeg', extensions: ['.jpg', '.jpeg'] },
  { mime: 'image/heif', extensions: ['.heif'] },
  { mime: 'image/heic', extensions: ['.heic'] },
  { mime: 'video/hevc', extensions: ['.hevc'] },
  { mime: 'application/msword', extensions: ['.doc'] },
  { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extensions: ['.docx'] },
];

const ALLOWED_FILE_TYPES = Object.fromEntries(
  FILE_TYPE_CONFIG.map(({ mime, extensions }) => [mime, extensions])
);

const ALLOWED_EXTENSIONS = FILE_TYPE_CONFIG.flatMap(({ extensions }) => extensions);

const EXTENSION_TO_MIME = Object.fromEntries(
  FILE_TYPE_CONFIG.flatMap(({ mime, extensions }) =>
    extensions.map(ext => [ext, mime])
  )
);

export const DROPZONE_ACCEPT_STRING = ALLOWED_EXTENSIONS.join(',');

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

function validateFile(file) {
  const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];

  if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Invalid file type for "${file.name}". Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`
    };
  }

  // Check MIME type if available
  if (file.type && !Object.keys(ALLOWED_FILE_TYPES).includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file format for "${file.name}"`
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
    return {
      valid: false,
      error: `"${file.name}" is too large. Maximum size: ${sizeMB}MB`
    };
  }

  if (file.size === 0) {
    return {
      valid: false,
      error: `"${file.name}" is empty`
    };
  }

  return { valid: true };
}

function inferContentType(filename) {
  const ext = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return EXTENSION_TO_MIME[ext] || 'application/octet-stream';
}

// Upload file to S3 with proper Content-Type derived from file extension.
// Content-Type is set implicitly via the File object's type property,
// rather than via an explicit header, to avoid triggering CORS preflight.
function uploadFileWithContentType(uploadUrl, fileObj) {
  let uploadFile = fileObj;
  if (!fileObj.type) {
    // If the file doesn't have a type, create a new File with inferred type
    const inferredType = inferContentType(fileObj.name);
    uploadFile = new File([fileObj], fileObj.name, { type: inferredType });
  }

  return fetch(uploadUrl, {
    method: "PUT",
    body: uploadFile,
    mode: "cors"
  });
}

// Retry logic with exponential backoff
async function uploadWithRetry(uploadFn, filename, maxRetries = 3) {
  if (maxRetries <= 0) {
    return await uploadFn();
  }
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await uploadFn();
    } catch (error) {
      if (attempt === maxRetries) throw error;

      const delay = Math.pow(2, attempt - 1) * 1000;
      logger.info(`Retrying "${filename}" in ${delay}ms (attempt ${attempt}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

function getUploadErrorMessage(error, context, filename) {
  if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
    return `Network error uploading "${filename}". Please check your connection.`;
  }

  if (error.response) {
    const status = error.response.status;
    if (status === 413) return `"${filename}" is too large`;
    if (status === 403) return `Upload not authorized for "${filename}"`;
    if (status >= 500) return `Server error uploading "${filename}". Please try again.`;
    return error.response.data?.error?.message || `Failed to upload "${filename}"`;
  }

  return `Failed to upload "${filename}": ${error.message || 'Unknown error'}`;
}

function showUploadSummary(results) {
  const successful = results.filter(r => r.success);
  const failed = results.filter(r => !r.success);

  if (failed.length === 0) {
    toast.success(
      <div>
        <div>Successfully uploaded {successful.length} file(s):</div>
        {successful.map((r, i) => <div key={i}>{"\u2713"} {r.filename}</div>)}
      </div>,
      { autoClose: 5000 }
    );
  } else if (successful.length === 0) {
    toast.error(
      <div>
        <div>Failed to upload {failed.length} file(s):</div>
        {failed.map((r, i) => <div key={i}>{"\u2717"} {r.filename}: {r.error}</div>)}
      </div>
    );
  } else {
    toast.warning(
      <div>
        <div>Uploaded {successful.length} file(s), {failed.length} failed:</div>
        {successful.map((r, i) => <div key={i}>{"\u2713"} {r.filename}</div>)}
        {failed.map((r, i) => <div key={i}>{"\u2717"} {r.filename}</div>)}
      </div>
    );
  }
}

export default function DocumentsList({
  documents,
  onDocumentsDropped,
  disableDrop
}) {
  const classes = useStyles();

  if (_.isEmpty(documents))
    return (
      <ListItem className={classes.subitemList}>
        <Typography gutterBottom>
          Customer has not uploaded any documents.
        </Typography>
        <DropZone disabled={disableDrop} onFilesDropped={onDocumentsDropped} accept={DROPZONE_ACCEPT_STRING} />
      </ListItem>
    );

  return (
    <GutterlessListItem className={classes.subitemList}>
      {documents.map((d) => (
        <Document key={d.id} doc={d} />
      ))}
      <DropZone disabled={disableDrop} onFilesDropped={onDocumentsDropped} accept={DROPZONE_ACCEPT_STRING} />
    </GutterlessListItem>
  );
}

function Document({ doc }) {
  const classes = useStyles();
  return (
    <div>
      ({doc.id}) {doc.name}
      {doc.description ? `: ${doc.description}` : ""}
      {doc.contentFiles.map((cf) => (
        <React.Fragment key={cf.id}>
          <a href={cf.presignedDownloadUrl}>
            <IconButton className={classes.icon}>
              <GetApp />
            </IconButton>
          </a>
          <a href={cf.presignedInlineUrl}>
            <IconButton className={classes.icon}>
              <OpenInBrowser />
            </IconButton>
          </a>
        </React.Fragment>
      ))}
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  subitemList: { flexDirection: "column", alignItems: "start", padding: 0 },
  icon: {
    marginLeft: theme.spacing(0.5)
  }
}));

export function useDocumentsState(enrollmentCase, entity, onUploadComplete) {
  enrollmentCase = enrollmentCase || {};
  const toastError = useToastError();

  const createDocumentsFromFiles = React.useCallback(
    async (files) => {
      const results = [];
      const filesToUpload = [];

      for (const file of files) {
        const validation = validateFile(file);
        if (!validation.valid) {
          results.push({ success: false, filename: file.name, error: validation.error });
        } else {
          filesToUpload.push(file);
        }
      }

      if (filesToUpload.length === 0 && results.length > 0) {
        showUploadSummary(results);
        return results;
      }

      // Files are uploaded sequentially to avoid overwhelming the server
      // and to provide clearer error attribution per file.
      for (const file of filesToUpload) {
        try {
          const presignedData = await uploadWithRetry(async () => {
            const response = await api.createPresignedDocumentUrl({
              customerId: enrollmentCase.customer.id,
              filename: file.name
            });
            return api.pickData(response);
          }, file.name);

          await uploadWithRetry(async () => {
            const response = await uploadFileWithContentType(presignedData.presignedUrl, file);
            if (!response.ok) {
              const error = new Error(`S3 upload failed: ${response.status}`);
              error.status = response.status;
              throw error;
            }
            return response;
          }, file.name);

          await api.addDocumentForReportingAccountEnrollment({
            id: enrollmentCase.id,
            s3_url: presignedData.presignedUrl,
            entity
          });

          results.push({ success: true, filename: file.name });

        } catch (error) {
          logger.exception(error);
          const errorMsg = getUploadErrorMessage(error, 'upload', file.name);
          results.push({ success: false, filename: file.name, error: errorMsg });
        }
      }

      showUploadSummary(results);

      const hasSuccess = results.some(r => r.success);
      if (hasSuccess && onUploadComplete) {
        try {
          await onUploadComplete();
        } catch (error) {
          logger.exception(error);
          toastError(error);
        }
      }

      return results;
    },
    [enrollmentCase.id, entity, onUploadComplete, toastError]
  );

  return { createDocumentsFromFiles };
}
